import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface ReportMailingSetting {
  id: string;
  tenantId: string;
  locationId: string;
  reportType: string;
  exportFormat: string;
  enabled: boolean;
  timezone: string;
  scheduleTime: string;
  fromEmail: string | null;
  toEmails: string[];
  ccEmails: string[];
  bccEmails: string[];
  lastSentAt: string | null;
  lastStatus: 'success' | 'failed' | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReportMailingOption {
  value: string;
  label: string;
}

export interface ReportMailingUpsert {
  tenantId: string;
  locationId: string;
  /** Reports sent on this schedule, one email each. */
  reportTypes: string[];
  exportFormat: string;
  enabled: boolean;
  timezone: string;
  scheduleTime: string;
  fromEmail: string | null;
  toEmails: string[];
  ccEmails: string[];
  bccEmails: string[];
}

export function useReportMailingSettings() {
  return useQuery({
    queryKey: ['report-mailing', 'settings'],
    queryFn: () => api.get<{ data: ReportMailingSetting[] }>('/admin/report-mailing/settings'),
  });
}

export function useReportMailingOptions() {
  return useQuery({
    queryKey: ['report-mailing', 'options'],
    queryFn: () =>
      api.get<{ data: { reportTypes: ReportMailingOption[]; exportFormats: ReportMailingOption[] } }>(
        '/admin/report-mailing/options',
      ),
  });
}

export function useUpsertReportMailingSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReportMailingUpsert) =>
      api.put<{ data: ReportMailingSetting[] }>('/admin/report-mailing/settings', body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['report-mailing', 'settings'] });
    },
  });
}

export function useSendTestReportMail() {
  return useMutation({
    mutationFn: (body: ReportMailingUpsert) =>
      api.post<{
        data: {
          results: { reportType: string; status: 'success' | 'failed'; error: string | null }[];
          recipient: string;
        };
      }>('/admin/report-mailing/test', body),
  });
}

export interface ReportMailingPreviewInput {
  tenantId: string;
  locationId: string;
  reportType: string;
  exportFormat: string;
  timezone: string;
  /** YYYY-MM-DD in the store's timezone; defaults to today there. */
  date?: string;
}

export interface ReportMailingPreview {
  subject: string;
  html: string;
  filename: string;
}

/** The email for one report + store + day, rendered but not sent or logged. */
export function useReportMailingPreview() {
  return useMutation({
    mutationFn: (body: ReportMailingPreviewInput) =>
      api.post<{ data: ReportMailingPreview }>('/admin/report-mailing/preview', body),
  });
}
